import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/* --------------------------------- tokens ---------------------------------- */

const T = {
  ink: "#000000", ink2: "#5A5A5A", ink3: "#8C8C8C", ink4: "#BFBFBF",
  hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5",
};
const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;

const FMCSA = {
  legalName: "Bayou City Carriers LLC", dba: "Bayou City Trucking",
  usdot: "3184427", mc: "MC-118934", entity: "Carrier", status: "Active",
  address: "4411 Navigation Blvd, Houston, TX 77011",
  powerUnits: 8, driversOnFile: 9, safetyRating: "Satisfactory",
  insurance: "BIPD on file — $1,000,000", mcs150: "Updated Feb 2026",
  registeredEmail: "o•••••@bayoucitycarriers.com", registeredPhone: "(713) •••-0155",
};

const LANGUAGES = ["English", "Spanish", "English and Spanish", "Other"];
const TRAILERS = ["53' dry van", "53' reefer", "48' flatbed", "Step deck", "Tanker", "Power only", "Other"];
const ZONES = ["Eastern", "Central", "Mountain", "Pacific", "Arizona (no DST)"];
const QUIET = ["9:00p – 6:00a", "10:00p – 5:00a", "8:00p – 7:00a", "No quiet hours"];
const ROLES = ["Dispatch", "After hours", "Billing and advances", "Operations manager", "Safety"];

const NUMBERS = [
  { n: "(713) 999-0140", area: "Houston, TX — local to your yard" },
  { n: "(281) 999-0177", area: "Houston metro" },
  { n: "(844) 999-0102", area: "Toll free" },
];

const CSV_SAMPLE = [
  { name: "Ivan Petrov", phone: "(713) 555-0128", language: "English", truckNo: "T-145", trailerType: "53' dry van", trailerNo: "V-8842", zone: "Central", quiet: "9:00p – 6:00a", ecName: "Lena Petrova", ecPhone: "(713) 555-0129", consent: true, truckDesc: "", note: "" },
  { name: "Andre Simmons", phone: "(832) 555-0193", language: "English", truckNo: "T-160", trailerType: "48' flatbed", trailerNo: "", zone: "Central", quiet: "9:00p – 6:00a", ecName: "Tanya Simmons", ecPhone: "(832) 555-0194", consent: true, truckDesc: "", note: "" },
  { name: "Luz Herrera", phone: "(956) 555-0111", language: "English and Spanish", truckNo: "T-172", trailerType: "53' reefer", trailerNo: "R-2210", zone: "Central", quiet: "10:00p – 5:00a", ecName: "Mateo Herrera", ecPhone: "(956) 555-0112", consent: true, truckDesc: "", note: "" },
];

/* ------------------------------- primitives -------------------------------- */

function Micro({ children, style }) {
  return <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
}

function Tag({ children, solid }) {
  return (
    <span style={{
      fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
      border: `1px solid ${T.ink}`, background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink,
      borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Soft({ children }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.hair}`, color: T.ink2, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}

function Button({ children, onClick, kind = "primary", disabled, size = "md" }) {
  const k = {
    primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` },
    quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` },
  }[kind];
  return (
    <button onClick={onClick} disabled={disabled} className="alf-btn" style={{
      ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15,
      padding: size === "sm" ? "6px 11px" : "9px 16px", borderRadius: 7,
      cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1,
    }}>{children}</button>
  );
}

const inputBase = {
  width: "100%", font: "inherit", fontSize: 14, color: T.ink, background: "#fff",
  border: `1px solid ${T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box",
};

function Field({ label, value, onChange, placeholder, isMono, hint, half, third, children }) {
  return (
    <div style={{ flex: third ? "1 1 30%" : half ? "1 1 46%" : "1 1 100%", minWidth: 0 }}>
      <Micro style={{ marginBottom: 5 }}>{label}</Micro>
      {children || <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={{ ...inputBase, fontFamily: isMono ? mono : sans }} />}
      {hint && <div style={{ fontSize: 12, color: T.ink3, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputBase, appearance: "none", cursor: "pointer" }}>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
}

function Row({ label, value, isMono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0" }}>
      <span style={{ fontSize: 13, color: T.ink3 }}>{label}</span>
      <span style={{ fontSize: 13, textAlign: "right", fontFamily: isMono ? mono : sans, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

function Check({ on, onChange, label, note }) {
  return (
    <button onClick={() => onChange(!on)} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", font: "inherit", width: "100%" }}>
      <span style={{
        width: 17, height: 17, borderRadius: 4, flexShrink: 0, marginTop: 1, boxSizing: "border-box",
        border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: "#fff",
        fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center",
      }}>{on ? "✓" : ""}</span>
      <span>
        <span style={{ display: "block", fontSize: 13.5, lineHeight: 1.45 }}>{label}</span>
        {note && <span style={{ display: "block", fontSize: 12, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{note}</span>}
      </span>
    </button>
  );
}

function Waveform({ active }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2.5, height: 14 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={active ? "alf-bar" : ""} style={{ width: 2, borderRadius: 1, background: T.ink, height: active ? 14 : 4, animationDelay: `${i * 0.11}s`, opacity: active ? 1 : 0.3 }} />
      ))}
    </span>
  );
}

function Steps({ list, current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
      {list.map((l, i) => (
        <React.Fragment key={l}>
          <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{
              width: 17, height: 17, borderRadius: 999, fontSize: 9.5, fontFamily: mono, boxSizing: "border-box",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: i < current ? T.ink : "#fff", color: i < current ? "#fff" : i === current ? T.ink : T.ink4,
              border: `${i === current ? 2 : 1}px solid ${i <= current ? T.ink : T.hair}`,
            }}>{i < current ? "✓" : i + 1}</span>
            <span style={{ fontSize: 12.5, fontWeight: i === current ? 600 : 400, color: i <= current ? T.ink : T.ink3 }}>{l}</span>
          </span>
          {i < list.length - 1 && <span style={{ width: 18, height: 1, background: T.hair }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Head({ title, note }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>{title}</div>
      {note && <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 5, lineHeight: 1.55, maxWidth: 520 }}>{note}</div>}
    </div>
  );
}

function Progress({ lines, at }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "4px 0" }}>
      {lines.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, opacity: i <= at ? 1 : 0.25 }}>
          {i < at ? <span style={{ width: 16, fontSize: 13 }}>✓</span> : i === at ? <span style={{ width: 16 }}><Waveform active /></span> : <span style={{ width: 16, textAlign: "center", color: T.ink4 }}>·</span>}
          <span style={{ fontSize: 13.5 }}>{s}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ step 1: carrier ---------------------------- */

function CarrierStep({ carrier, setCarrier, onNext }) {
  const [mode, setMode] = useState("mc");
  const [num, setNum] = useState("");
  const [phase, setPhase] = useState(carrier.found ? (carrier.verified ? (carrier.callNumber ? "number" : "number") : "verify") : "idle");
  const [line, setLine] = useState(0);
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("email");
  const LOOKUP = ["Querying FMCSA", "Checking operating authority", "Reading insurance on file"];

  useEffect(() => {
    if (phase !== "looking") return;
    if (line < LOOKUP.length) { const t = setTimeout(() => setLine((n) => n + 1), 480); return () => clearTimeout(t); }
    const t = setTimeout(() => { setCarrier({ ...carrier, ...FMCSA, found: true, lookedUpBy: mode, entered: num }); setPhase("found"); }, 420);
    return () => clearTimeout(t);
  }, [phase, line]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Start with your authority"
        note="Enter your MC or USDOT number and we'll pull the rest from FMCSA. Newer authorities are DOT-only, and FMCSA is phasing MC numbers out — either works." />

      {(phase === "idle" || phase === "looking") && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
          <div style={{ display: "flex", border: `1px solid ${T.hair}`, borderRadius: 7, overflow: "hidden", width: "fit-content" }}>
            {[["mc", "MC number"], ["dot", "USDOT number"]].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)} style={{
                font: "inherit", fontSize: 12.5, fontWeight: 500, padding: "6px 12px", border: "none", cursor: "pointer",
                background: mode === v ? T.ink : "#fff", color: mode === v ? "#fff" : T.ink2,
              }}>{l}</button>
            ))}
          </div>
          <Field label={mode === "mc" ? "MC number" : "USDOT number"} value={num} onChange={setNum} placeholder={mode === "mc" ? "MC-118934" : "3184427"} isMono />
          {phase === "idle"
            ? <div><Button disabled={!num.trim()} onClick={() => { setLine(0); setPhase("looking"); }}>Look it up</Button></div>
            : <Progress lines={LOOKUP} at={line} />}
        </div>
      )}

      {phase !== "idle" && phase !== "looking" && (
        <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.hairSoft}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{carrier.legalName}</span>
            <Tag solid>{carrier.status}</Tag>
            {carrier.verified && <Soft>verified</Soft>}
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: T.ink3 }}>{carrier.mc} · USDOT {carrier.usdot}</span>
          </div>
          <div style={{ padding: "12px 18px 16px" }}>
            <Row label="Doing business as" value={carrier.dba} />
            <Row label="Physical address" value={carrier.address} />
            <Row label="Power units" value={String(carrier.powerUnits)} isMono />
            <Row label="Drivers on file" value={String(carrier.driversOnFile)} isMono />
            <Row label="Safety rating" value={carrier.safetyRating} />
            <Row label="Insurance" value={carrier.insurance} />
            <Row label="MCS-150" value={carrier.mcs150} />
          </div>
        </div>
      )}

      {phase === "found" && (
        <div className="alf-fade" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button onClick={() => setPhase("verify")}>That's us</Button>
          <Button kind="quiet" onClick={() => { setPhase("idle"); setCarrier({ ...carrier, found: false }); }}>Not us, search again</Button>
        </div>
      )}

      {phase === "verify" && (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14, maxWidth: 480 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Prove the authority is yours</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>
              MC hijacking is common enough that we won't take your word for it. We'll send a code to the contact FMCSA has on file for this authority — not one you type in here.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["email", `Email ${FMCSA.registeredEmail}`], ["phone", `Call ${FMCSA.registeredPhone}`]].map(([v, l]) => (
              <button key={v} onClick={() => setSentTo(v)} style={{
                font: "inherit", fontSize: 12.5, padding: "7px 11px", borderRadius: 7, cursor: "pointer",
                border: `1px solid ${sentTo === v ? T.ink : T.hair}`, background: sentTo === v ? T.ink : "#fff", color: sentTo === v ? "#fff" : T.ink2,
              }}>{l}</button>
            ))}
          </div>
          <Field label="Six-digit code" value={code} onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" isMono
            hint="Any six digits will work in this prototype." />
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={code.length < 6} onClick={() => { setCarrier({ ...carrier, verified: true, authorities: [{ name: carrier.legalName, mc: carrier.mc, verified: true }] }); setPhase("number"); }}>Verify</Button>
            <Button kind="quiet" onClick={() => setPhase("found")}>Back</Button>
          </div>
        </div>
      )}

      {phase === "number" && (
        <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>The number Alfred calls from</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 520 }}>
              We provision this number to your verified authority and register it for STIR/SHAKEN attestation. Unregistered numbers get labeled "Spam Likely" by the carriers, and a driver who doesn't recognize the number doesn't answer.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {NUMBERS.map((n) => (
                <button key={n.n} onClick={() => setCarrier({ ...carrier, callNumber: n.n })} style={{
                  display: "flex", alignItems: "center", gap: 12, textAlign: "left", font: "inherit", cursor: "pointer",
                  padding: "11px 14px", borderRadius: 8, background: "#fff",
                  border: `1px solid ${carrier.callNumber === n.n ? T.ink : T.hair}`,
                }}>
                  <span style={{ fontFamily: mono, fontSize: 14 }}>{n.n}</span>
                  <span style={{ fontSize: 12.5, color: T.ink3 }}>{n.area}</span>
                  {carrier.callNumber === n.n && <span style={{ marginLeft: "auto" }}><Tag solid>attestation A</Tag></span>}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
              <Field half label="Caller ID name" value={carrier.callerId} onChange={(v) => setCarrier({ ...carrier, callerId: v })} placeholder={carrier.dba}
                hint="Registered to CNAM so it shows as a name, not a number." />
              <Field half label="Office phone" value={carrier.officePhone} onChange={(v) => setCarrier({ ...carrier, officePhone: v })} placeholder="(713) 555-0170" isMono
                hint="Where Alfred tells people to call back." />
            </div>
          </div>

          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Office details</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
              <Field half label="Office time zone">
                <Select value={carrier.officeZone} onChange={(v) => setCarrier({ ...carrier, officeZone: v })} options={ZONES} />
              </Field>
              <Field half label="Who holds the certificate of insurance" value={carrier.coiHolder} onChange={(v) => setCarrier({ ...carrier, coiHolder: v })}
                placeholder="Name or agency" hint="Brokers ask for a current COI constantly. Alfred routes those asks here instead of guessing." />
            </div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 10, lineHeight: 1.45 }}>
              Call plans are scheduled in office time and checked against each driver's home zone before Alfred dials.
            </div>
          </div>

          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Authorities</span>
              <Soft>{carrier.authorities.length} on file</Soft>
            </div>
            {carrier.authorities.map((a) => (
              <div key={a.mc} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: `1px solid ${T.hairSoft}`, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 180 }}>{a.name}</span>
                <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{a.mc}</span>
                {a.verified && <Soft>verified</Soft>}
              </div>
            ))}
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 12, lineHeight: 1.5 }}>
              Running more than one authority — a second DBA, a leased-on entity — means each gets its own verification and its own calling number. Drivers can be assigned to either.
            </div>
            <div style={{ marginTop: 12 }}>
              <Button size="sm" kind="quiet" onClick={() => setCarrier({ ...carrier, authorities: [...carrier.authorities, { name: "Second authority (unverified)", mc: "MC-000000", verified: false }] })}>
                Add another authority
              </Button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!carrier.callNumber} onClick={onNext}>Add drivers</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ step 2: drivers ---------------------------- */

const BLANK_DRIVER = {
  name: "", phone: "", language: "English", truckNo: "", truckDesc: "",
  trailerType: "53' dry van", trailerNo: "", zone: "Central", quiet: "9:00p – 6:00a",
  ecName: "", ecPhone: "", consent: false, note: "",
};

function DriverForm({ value, onChange, onSave, onCancel, saveLabel }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const ready = value.name.trim() && value.phone.trim() && value.truckNo.trim() && value.ecName.trim() && value.ecPhone.trim() && value.consent;
  return (
    <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <Field half label="Driver name" value={value.name} onChange={set("name")} placeholder="Required" />
        <Field half label="Cell phone" value={value.phone} onChange={set("phone")} placeholder="Required" isMono />
        <Field half label="Language"><Select value={value.language} onChange={set("language")} options={LANGUAGES} /></Field>
        <Field half label="Truck number" value={value.truckNo} onChange={set("truckNo")} placeholder="T-118" isMono />
      </div>

      <div>
        <Micro style={{ marginBottom: 8 }}>Equipment</Micro>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <Field half label="Truck make and model" value={value.truckDesc} onChange={set("truckDesc")} placeholder="Optional — 2021 Cascadia" />
          <Field half label="Trailer type"><Select value={value.trailerType} onChange={set("trailerType")} options={TRAILERS} /></Field>
          <Field half label="Trailer number" value={value.trailerNo} onChange={set("trailerNo")} placeholder="Optional — blank for drop and hook" isMono />
        </div>
      </div>

      <div>
        <Micro style={{ marginBottom: 8 }}>When Alfred can call</Micro>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <Field half label="Home time zone"><Select value={value.zone} onChange={set("zone")} options={ZONES} /></Field>
          <Field half label="Quiet hours"><Select value={value.quiet} onChange={set("quiet")} options={QUIET} /></Field>
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
          Call times are scheduled in your office time zone and checked against this one. A 6:30a Central call is 4:30a for a Pacific driver — Alfred holds it.
        </div>
      </div>

      <div>
        <Micro style={{ marginBottom: 8 }}>Emergency contact</Micro>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <Field half label="Name" value={value.ecName} onChange={set("ecName")} placeholder="Required" />
          <Field half label="Phone" value={value.ecPhone} onChange={set("ecPhone")} placeholder="Required" isMono />
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
          Last rung on the escalation ladder. Only used when a driver is unreachable across repeated attempts, and only by a person — never by Alfred.
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${T.hairSoft}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <Micro>Consent</Micro>
        <Check on={value.consent} onChange={set("consent")}
          label="This driver has been told Alfred is an AI, that calls are recorded and transcribed, and that anything they say about how they're doing may be passed to the office."
          note="Required. Your drivers cross into California, Illinois, Florida, Pennsylvania and Washington — all two-party-consent states. Alfred discloses on every call regardless, but you need the acknowledgment on file." />
      </div>

      <Field label="Anything Alfred should know" value={value.note} onChange={set("note")} placeholder="Optional — best time to call, hard of hearing, speaks little English" />

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Button disabled={!ready} onClick={onSave}>{saveLabel}</Button>
        {onCancel && <Button kind="quiet" onClick={onCancel}>Cancel</Button>}
        {!ready && <span style={{ fontSize: 12, color: T.ink3 }}>Name, cell, truck number, emergency contact, and the consent box are required.</span>}
      </div>
    </div>
  );
}

function DriversStep({ carrier, drivers, setDrivers, onBack, onNext }) {
  const [draft, setDraft] = useState(BLANK_DRIVER);
  const [adding, setAdding] = useState(drivers.length === 0);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState(BLANK_DRIVER);
  const [importing, setImporting] = useState(null);
  const fileRef = useRef(null);

  function onFile(e) {
    const f = e.target.files && e.target.files[0];
    setImporting({ name: f ? f.name : "drivers.csv", rows: CSV_SAMPLE.map((r) => ({ ...r, id: "d" + Math.random().toString(36).slice(2, 7), consent: false })) });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Who's driving?"
        note="Alfred needs a name, cell, truck number, and an emergency contact before it can call anyone. Trailer details are optional." />
      {carrier.driversOnFile > 0 && (
        <div style={{ fontSize: 12.5, color: T.ink3, marginTop: -12 }}>
          FMCSA lists {carrier.driversOnFile} drivers for {carrier.dba}. You've added {drivers.length}.
        </div>
      )}

      {importing && (
        <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>{importing.rows.length} drivers found in {importing.name}</span>
            <Soft>preview</Soft>
          </div>
          <div style={{ marginTop: 12 }}>
            {importing.rows.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 12, padding: "8px 0", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 140 }}>{r.name}</span>
                <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{r.phone}</span>
                <span style={{ fontSize: 12.5, color: T.ink3 }}>{r.truckNo} · {r.trailerType}</span>
                <span style={{ marginLeft: "auto" }}><Soft>no emergency contact</Soft></span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 12, lineHeight: 1.5 }}>
            Imported drivers come in without consent on file. Alfred won't call them until you open each one and confirm they've been told — that's a legal requirement, not a formality.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <Button onClick={() => { setDrivers([...drivers, ...importing.rows]); setImporting(null); setAdding(false); }}>Import {importing.rows.length} drivers</Button>
            <Button kind="quiet" onClick={() => setImporting(null)}>Discard</Button>
          </div>
        </div>
      )}

      {drivers.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
          {drivers.map((d, i) => (
            <div key={d.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", flexWrap: "wrap" }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, border: `1px solid ${T.hair}`, fontSize: 11.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {d.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </span>
                <span style={{ flex: 1, minWidth: 160 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{d.name}</span>
                    {!d.consent && <Tag>consent needed</Tag>}
                  </span>
                  <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                    <span style={{ fontFamily: mono }}>{d.phone}</span> · {d.truckNo} · {d.trailerType}{d.trailerNo ? ` ${d.trailerNo}` : ""} · {d.zone}
                  </span>
                </span>
                <button onClick={() => { setEditId(editId === d.id ? null : d.id); setEditDraft(d); }}
                  style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
                  {editId === d.id ? "Close" : d.consent ? "Edit" : "Finish"}
                </button>
                <button onClick={() => setDrivers(drivers.filter((x) => x.id !== d.id))} style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Remove</button>
              </div>
              {editId === d.id && (
                <div style={{ padding: "0 18px 18px" }}>
                  <DriverForm value={editDraft} onChange={setEditDraft} saveLabel="Save changes"
                    onSave={() => { setDrivers(drivers.map((x) => (x.id === d.id ? { ...editDraft, id: d.id } : x))); setEditId(null); }}
                    onCancel={() => setEditId(null)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <DriverForm value={draft} onChange={setDraft} saveLabel="Add driver"
          onSave={() => { setDrivers([...drivers, { ...draft, id: "d" + Math.random().toString(36).slice(2, 7) }]); setDraft(BLANK_DRIVER); setAdding(false); }}
          onCancel={drivers.length ? () => { setAdding(false); setDraft(BLANK_DRIVER); } : null} />
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button kind="quiet" onClick={() => setAdding(true)}>Add another driver</Button>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>or</span>
          <button onClick={() => fileRef.current && fileRef.current.click()} style={{ font: "inherit", fontSize: 13, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
            upload a driver list
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.txt" onChange={onFile} style={{ display: "none" }} />
        </div>
      )}

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button disabled={drivers.length === 0} onClick={onNext}>Next: contacts</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* ----------------------------- step 3: contacts ---------------------------- */

const BLANK_CONTACT = { name: "", role: "Dispatch", phone: "", hours: "Mon–Fri, 6:00a–6:00p", afterHours: false, can: ["ops"] };

function ContactsStep({ contacts, setContacts, onBack, onNext }) {
  const [draft, setDraft] = useState(BLANK_CONTACT);
  const [adding, setAdding] = useState(contacts.length === 0);
  const ready = draft.name.trim() && draft.phone.trim();
  const hasAfterHours = contacts.some((c) => c.afterHours);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Who does Alfred hand off to?"
        note="When a driver asks for money, raises a safety concern, or stops answering, Alfred stops and calls a person. These are the people it calls, and each load picks from this list." />

      {contacts.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
          {contacts.map((c, i) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 16 }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 160 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{c.name}</span>
                  <Soft>{c.role}</Soft>
                  {c.afterHours && <Tag>after hours</Tag>}
                  {c.can.map((k) => <Soft key={k}>{k}</Soft>)}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                  <span style={{ fontFamily: mono }}>{c.phone}</span> · {c.hours}
                </span>
              </span>
              <button onClick={() => setContacts(contacts.filter((x) => x.id !== c.id))} style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <Field half label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Required" />
            <Field half label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} placeholder="Required" isMono />
            <Field half label="Role"><Select value={draft.role} onChange={(v) => setDraft({ ...draft, role: v })} options={ROLES} /></Field>
            <Field half label="Available hours" value={draft.hours} onChange={(v) => setDraft({ ...draft, hours: v })} placeholder="Mon–Fri, 6:00a–6:00p" />
          </div>
          <div>
            <Micro style={{ marginBottom: 7 }}>What they can clear</Micro>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[["ops", "Ops and appointments"], ["billing", "Money and advances"], ["safety", "Incidents and claims"]].map(([k, l]) => {
                const on = draft.can.includes(k);
                return (
                  <button key={k} onClick={() => setDraft({ ...draft, can: on ? draft.can.filter((x) => x !== k) : [...draft.can, k] })}
                    style={{ font: "inherit", fontSize: 12.5, padding: "6px 11px", borderRadius: 7, cursor: "pointer",
                      border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: on ? "#fff" : T.ink2 }}>{l}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
              Being on the ladder means Alfred calls them. This decides what they're allowed to sign off on once they pick up — a dispatcher can reset an appointment without being able to approve an advance.
            </div>
          </div>
          <Check on={draft.afterHours} onChange={(v) => setDraft({ ...draft, afterHours: v })}
            label="This is the after-hours fallback"
            note="Gets the handoff when nobody else is on shift. Freight doesn't stop at 6pm — if no one holds this, overnight handoffs sit until morning." />
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!ready} onClick={() => { setContacts([...contacts, { ...draft, id: "s" + Math.random().toString(36).slice(2, 7) }]); setDraft(BLANK_CONTACT); setAdding(false); }}>Add contact</Button>
            {contacts.length > 0 && <Button kind="quiet" onClick={() => { setAdding(false); setDraft(BLANK_CONTACT); }}>Cancel</Button>}
          </div>
        </div>
      ) : (
        <Button kind="quiet" onClick={() => setAdding(true)}>Add another contact</Button>
      )}

      {contacts.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "14px 18px" }}>
          <Micro style={{ marginBottom: 8 }}>Escalation ladder</Micro>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            {[["Load's primary", "0m"], ["Second contact", "+20m"], [contacts.find((c) => c.role === "Operations manager") ? contacts.find((c) => c.role === "Operations manager").name : "Operations manager", "+45m"], ["Driver's emergency contact", "+90m"]].map(([n, t], i) => (
              <React.Fragment key={n}>
                {i > 0 && <span style={{ width: 12, height: 1, background: T.hair }} />}
                <span style={{ fontSize: 11.5, padding: "2px 7px", borderRadius: 4, border: `1px solid ${T.hair}`, whiteSpace: "nowrap" }}>{n} {t}</span>
              </React.Fragment>
            ))}
          </div>
          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 10, lineHeight: 1.5 }}>
            A handoff climbs this ladder until someone picks it up. {hasAfterHours ? "Your after-hours contact takes anything outside office hours." : "You haven't set an after-hours fallback — overnight handoffs will wait for the morning."}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button disabled={contacts.length === 0} onClick={onNext}>Review</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* ------------------------------ step 4: review ----------------------------- */

function ReviewStep({ carrier, drivers, contacts, onBack, onFinish, done }) {
  const missingConsent = drivers.filter((d) => !d.consent);

  if (done) {
    return (
      <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start", paddingTop: 8 }}>
        <Waveform active />
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>{carrier.dba} is set up</div>
        <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55, maxWidth: 480 }}>
          {drivers.length} driver{drivers.length === 1 ? "" : "s"} reachable from {carrier.callNumber}, {contacts.length} contact{contacts.length === 1 ? "" : "s"} on the ladder.
        </div>
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden", width: "100%", maxWidth: 480 }}>
          {[["Add your first load", "Upload a rate confirmation. Alfred reads the stops and builds the call plan around them."],
            ["Connect your ELD", "Motive or Samsara. Without it Alfred can't tell whether a driver is rolling, and it won't call anyone in motion."],
            ["Connect your TMS", "Optional. With it, load numbers resolve on their own."]].map(([t, d], i) => (
            <div key={t} style={{ display: "flex", gap: 12, padding: "13px 16px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{t}</div>
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{d}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={onFinish}>Add your first load</Button>
          <Button kind="quiet" onClick={onFinish}>Connect an ELD</Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Check it over" note="All of this stays editable later." />

      {missingConsent.length > 0 && (
        <div style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: "13px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <Tag solid>hold</Tag>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{missingConsent.length} driver{missingConsent.length === 1 ? "" : "s"} without consent on file</span>
          </div>
          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
            {missingConsent.map((d) => d.name).join(", ")} — Alfred won't dial them until you confirm they've been told it's an AI and that calls are recorded. Everything else about them is saved.
          </div>
        </div>
      )}

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "14px 18px 16px" }}>
        <Micro style={{ marginBottom: 6 }}>Carrier</Micro>
        <Row label={carrier.legalName} value={carrier.mc} isMono />
        <Row label="USDOT" value={carrier.usdot} isMono />
        <Row label="Authority verified" value={carrier.verified ? "Yes, by code to the FMCSA contact" : "No"} />
        <Row label="Calling from" value={`${carrier.callNumber} · attestation A`} isMono />
        <Row label="Caller ID" value={carrier.callerId || carrier.dba} />
        <Row label="Callback number" value={carrier.officePhone || "Not set"} isMono />
        <Row label="Office time zone" value={carrier.officeZone} />
        <Row label="COI held by" value={carrier.coiHolder || "Not set"} />
        <Row label="Authorities" value={`${carrier.authorities.length} on file`} />
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.hairSoft}` }}><Micro>{drivers.length} driver{drivers.length === 1 ? "" : "s"}</Micro></div>
        {drivers.map((d, i) => (
          <div key={d.id} style={{ display: "flex", gap: 12, padding: "12px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 500, minWidth: 140 }}>{d.name}</span>
            <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{d.phone}</span>
            {!d.consent && <Tag>consent needed</Tag>}
            <span style={{ fontSize: 12.5, color: T.ink3, marginLeft: "auto" }}>{d.truckNo} · {d.zone} · quiet {d.quiet}</span>
          </div>
        ))}
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.hairSoft}` }}><Micro>{contacts.length} contact{contacts.length === 1 ? "" : "s"}</Micro></div>
        {contacts.map((c, i) => (
          <div key={c.id} style={{ display: "flex", gap: 12, padding: "12px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 500, minWidth: 140 }}>{c.name}</span>
            <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{c.phone}</span>
            {c.afterHours && <Tag>after hours</Tag>}
            <span style={{ fontSize: 12.5, color: T.ink3, marginLeft: "auto" }}>{c.role} · {c.hours}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button onClick={onFinish}>Finish setup</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   PROTOTYPE BANNER — non-negotiable while this screen runs on seed data.
   Every number below is hard-coded in this file: the FMCSA record, the drivers,
   the loads, the ELD positions, the call transcripts. Nothing reaches the API,
   no call is placed, nothing persists past a refresh. An operator screen that
   looks live and isn't is the exact failure this platform keeps paying for, so
   it says so at the top rather than in a footnote.
   ───────────────────────────────────────────────────────────────────────────── */
function PrototypeBanner({ what }) {
  return (
    <div style={{
      background: "#000", color: "#fff", fontFamily: sans, fontSize: 12.5,
      padding: "7px 22px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
    }}>
      <span style={{
        fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
        border: "1px solid #fff", borderRadius: 3, padding: "1px 5px"
      }}>prototype</span>
      <span>{what} Sample data — nothing here is connected to live calls or saved.</span>
      <Link to="/" style={{ marginLeft: "auto", color: "#fff", fontSize: 12.5, textUnderlineOffset: 3 }}>
        ← Back to the dashboard
      </Link>
    </div>
  );
}

/* ----------------------------------- app ----------------------------------- */

export default function AlfredOnboarding() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [carrier, setCarrier] = useState({ mc: "", found: false, verified: false, callNumber: "", officePhone: "", callerId: "", officeZone: "Central", coiHolder: "", authorities: [] });
  const [drivers, setDrivers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const LIST = ["Carrier", "Drivers", "Contacts", "Review"];

  return (
    <div className="alf-root" style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        .alf-bar { animation: alfBar 1s ease-in-out infinite; transform-origin: bottom; }
        @keyframes alfBar { 0%,100% { transform: scaleY(.28);} 50% { transform: scaleY(1);} }
        .alf-fade { animation: alfFade .26s cubic-bezier(.4,0,.2,1); }
        @keyframes alfFade { from { opacity:0; transform: translateY(4px);} to { opacity:1; transform:none;} }
        .alf-btn { transition: opacity .15s, transform .1s; }
        .alf-btn:active { transform: scale(.975); }
        .alf-root input::placeholder { color:${T.ink4}; }
        .alf-root input:focus, .alf-root select:focus { border-color:#000 !important; }
        .alf-root button:focus-visible, .alf-root input:focus-visible, .alf-root select:focus-visible { outline:2px solid #000; outline-offset:2px; }
        @media (prefers-reduced-motion: reduce) { .alf-bar,.alf-fade { animation:none !important; } }
      `}</style>

      <PrototypeBanner what="Carrier setup walkthrough." />

      <header style={{ borderBottom: `1px solid ${T.hair}`, background: "#fff" }}>
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "0 22px", height: 54, display: "flex", alignItems: "center", gap: 20 }}>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</span>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Setup</span>
        </div>
      </header>

      <main style={{ maxWidth: 780, margin: "0 auto", padding: "26px 22px 70px" }}>
        <div style={{ marginBottom: 24 }}><Steps list={LIST} current={done ? 4 : step} /></div>
        <div style={{ background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, padding: "26px 26px 28px" }}>
          {step === 0 && <CarrierStep carrier={carrier} setCarrier={setCarrier} onNext={() => setStep(1)} />}
          {step === 1 && <DriversStep carrier={carrier} drivers={drivers} setDrivers={setDrivers} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
          {step === 2 && <ContactsStep contacts={contacts} setContacts={setContacts} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
          {step === 3 && <ReviewStep carrier={carrier} drivers={drivers} contacts={contacts} done={done} onBack={() => setStep(2)} onFinish={() => setDone(true)} />}
        </div>
      </main>
    </div>
  );
}
